/**
 * FilterPanel Component
 * 
 * Collapsible panel containing all filter controls for the Apply Matching tab.
 * Includes Assigned User, Status, and Score Range filters.
 */

import React from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import type { FilterState, UniqueUser, ScoreRange } from '../../utils/matchFilters';
import SearchableUserDropdown from './SearchableUserDropdown';
import StatusFilterDropdown from './StatusFilterDropdown';
import ScoreRangeFilter from './ScoreRangeFilter';

interface FilterPanelProps {
  filterState: FilterState;
  uniqueUsers: UniqueUser[];
  uniqueStatuses: Array<{ value: string; label: string }>;
  onAssignedUserChange: (userId: string | null) => void;
  onStatusChange: (status: string | null) => void;
  onScoreRangeChange: (range: ScoreRange) => void;
  onTogglePanel: () => void;
  onClearFilters: () => void;
  activeFilterCount: number;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filterState,
  uniqueUsers,
  uniqueStatuses,
  onAssignedUserChange,
  onStatusChange,
  onScoreRangeChange,
  onTogglePanel,
  onClearFilters,
  activeFilterCount
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-3">
      {/* Header with toggle and clear buttons */}
      <div className="px-3 py-2 flex items-center justify-between border-b border-gray-200">
        <button
          onClick={onTogglePanel}
          className="flex items-center space-x-1.5 text-xs font-medium text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1.5 py-1"
          aria-expanded={filterState.isPanelExpanded}
          aria-label={filterState.isPanelExpanded ? 'Collapse filters' : 'Expand filters'}
        >
          {filterState.isPanelExpanded ? (
            <ChevronUpIcon className="w-3.5 h-3.5" />
          ) : (
            <ChevronDownIcon className="w-3.5 h-3.5" />
          )}
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700 bg-blue-100 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>

        <button
          onClick={onClearFilters}
          className="text-xs text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
          aria-label="Clear all filters"
        >
          Clear
        </button>
      </div>

      {/* Filter controls - shown when expanded */}
      {filterState.isPanelExpanded && (
        <div className="px-3 py-3 space-y-3">
          {/* Dropdowns row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Assigned To
              </label>
              <SearchableUserDropdown
                users={uniqueUsers}
                selectedUserId={filterState.assignedUserId}
                onChange={onAssignedUserChange}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Status
              </label>
              <StatusFilterDropdown
                selectedStatus={filterState.status}
                onChange={onStatusChange}
                availableStatuses={uniqueStatuses}
              />
            </div>
          </div>

          {/* Score range filter */}
          <div>
            <ScoreRangeFilter
              selectedRange={filterState.scoreRange}
              onChange={onScoreRangeChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
